// src/models/tenantModel.js - Updated Frontend Tenant Model with Owner Support
import api from '../services/api';




// Tenant Management Functions

// Get all tenants with filters and owner information
export const getAllTenants = async (filters = {}) => {
    try {
        const response = await api.get('/tenants', { params: filters });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching tenants' };
    }
};

// Get tenant by ID with owner information
export const getTenant = async (id) => {
    try {
        const response = await api.get(`/tenants/${id}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching tenant' };
    }
};

// Get tenants by owner ID
export const getTenantsByOwner = async (ownerId) => {
    try {
        const response = await api.get(`/tenants/by-owner/${ownerId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching tenants by owner' };
    }
};

// Create new tenant
export const createTenant = async (tenantData) => {
    try {
        // Map frontend field names to backend field names
        const payload = {
            name: tenantData.name || `${tenantData.firstName} ${tenantData.lastName}`,
            phone: tenantData.phone,
            rent: parseFloat(tenantData.rent) || 0,
            discount: parseFloat(tenantData.discount) || 0,
            serviceFee: parseFloat(tenantData.serviceFee) || 0,
            officeCommission: parseFloat(tenantData.officeCommission) || 0,
            insurance: parseFloat(tenantData.insurance) || 0,
            contractStartDate: tenantData.contractStartDate,
            contractEndDate: tenantData.contractEndDate,
            paymentDate: tenantData.paymentDate,
            apartmentId: tenantData.apartmentId,
            preferredLanguage: tenantData.preferredLanguage || 'en'
        };

        if (tenantData.notes) payload.notes = tenantData.notes;

        const response = await api.post('/tenants', payload);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error creating tenant' };
    }
};

// Update tenant
export const updateTenant = async (id, tenantData) => {
    try {
        // Map frontend field names to backend field names where necessary
        const payload = {
            name: tenantData.name || `${tenantData.firstName} ${tenantData.lastName}`,
            phone: tenantData.phone,
            rent: parseFloat(tenantData.rent),
            discount: parseFloat(tenantData.discount),
            serviceFee: parseFloat(tenantData.serviceFee),
            officeCommission: parseFloat(tenantData.officeCommission),
            insurance: parseFloat(tenantData.insurance),
            contractStartDate: tenantData.contractStartDate,
            contractEndDate: tenantData.contractEndDate,
            paymentDate: tenantData.paymentDate,
            preferredLanguage: tenantData.preferredLanguage
        };

        // Add optional fields
        if (tenantData.paymentEndDate) payload.paymentEndDate = tenantData.paymentEndDate;
        if (tenantData.rentStatus) payload.rentStatus = tenantData.rentStatus;
        if (tenantData.notes) payload.notes = tenantData.notes;

        // Only include fields that are present in the update data
        Object.keys(payload).forEach(key =>
            payload[key] === undefined && delete payload[key]
        );

        const response = await api.put(`/tenants/${id}`, payload);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error updating tenant' };
    }
};

// Delete tenant
export const deleteTenant = async (id) => {
    try {
        const response = await api.delete(`/tenants/${id}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error deleting tenant' };
    }
};

// Update tenant status (rent status)
export const updateTenantStatus = async (id, rentStatus) => {
    try {
        const response = await api.put(`/tenants/${id}/status`, { rentStatus });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error updating tenant status' };
    }
};

// Search tenants with owner information
export const searchTenants = async (searchTerm) => {
    try {
        const response = await api.get(`/tenants/search?q=${encodeURIComponent(searchTerm)}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error searching tenants' };
    }
};

// Get tenant statistics
export const getTenantStats = async () => {
    try {
        const response = await api.get('/tenants/stats');
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching tenant stats' };
    }
};

// Get tenants with overdue rent
export const getTenantsWithOverdueRent = async () => {
    try {
        const response = await api.get('/tenants/overdue');
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching overdue tenants' };
    }
};

// Get tenant's leases
export const getTenantLeases = async (tenantId) => {
    try {
        const response = await api.get(`/tenants/${tenantId}/leases`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching tenant leases' };
    }
};

// Get lease by ID
export const getLeaseById = async (leaseId) => {
    try {
        const response = await api.get(`/tenants/leases/${leaseId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching lease details' };
    }
};

// End lease
export const endLease = async (leaseId, endData) => {
    try {
        const response = await api.put(`/tenants/leases/${leaseId}/end`, endData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error ending lease' };
    }
};

// Extend lease
export const extendLease = async (id, newEndDate) => {
    try {
        const response = await api.post(`/tenants/${id}/extend`, { newEndDate });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error extending lease' };
    }
};

// Renew lease
export const renewLease = async (id, data) => {
    try {
        const response = await api.post(`/tenants/${id}/renew`, data);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error renewing lease' };
    }
};

// Transfer apartment
export const transferApartment = async (id, data) => {
    try {
        const response = await api.post(`/tenants/${id}/transfer`, data);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error transferring apartment' };
    }
};

// Get lease payments
export const getRentPayments = async (leaseId, options = {}) => {
    try {
        const response = await api.get(`/tenants/leases/${leaseId}/payments`, { params: options });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching rent payments' };
    }
};

// Record rent payment
export const recordRentPayment = async (leaseId, paymentData) => {
    try {
        // Map frontend payment data to backend structure
        const payload = {
            amount: parseFloat(paymentData.amount),
            paymentDate: paymentData.paymentDate,
            months: Array.isArray(paymentData.months) ? paymentData.months : [
                paymentData.paymentPeriodStart,
                paymentData.paymentPeriodEnd
            ],
            paymentType: paymentData.paymentType || 'rent',
            notes: paymentData.notes || ''
        };

        const response = await api.post(`/tenants/leases/${leaseId}/payments`, payload);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error recording payment' };
    }
};

// Get tenant maintenance requests
export const getTenantMaintenanceRequests = async (tenantId, options = {}) => {
    try {
        const response = await api.get(`/tenants/${tenantId}/maintenance`);
        // Backend doesn't support this, but we'll handle the 501 response gracefully
        return { requests: [] };
    } catch (error) {
        if (error.response?.status === 501) {
            return { requests: [], message: 'Maintenance requests not supported in current database structure' };
        }
        throw error.response?.data || { success: false, message: 'Error fetching maintenance requests' };
    }
};

// Create maintenance request
export const createMaintenanceRequest = async (tenantId, requestData) => {
    try {
        const response = await api.post(`/tenants/${tenantId}/maintenance`, requestData);
        return response.data;
    } catch (error) {
        if (error.response?.status === 501) {
            throw { success: false, message: 'Maintenance requests not supported in current database structure' };
        }
        throw error.response?.data || { success: false, message: 'Error creating maintenance request' };
    }
};

// Update maintenance request status
export const updateMaintenanceRequestStatus = async (requestId, updateData) => {
    try {
        const response = await api.put(`/tenants/maintenance/${requestId}`, updateData);
        return response.data;
    } catch (error) {
        if (error.response?.status === 501) {
            throw { success: false, message: 'Maintenance requests not supported in current database structure' };
        }
        throw error.response?.data || { success: false, message: 'Error updating maintenance request' };
    }
};

// Helper Functions

// Format tenant display name
export const formatTenantDisplayName = (tenant) => {
    if (!tenant) return '';

    if (tenant.name) return tenant.name;

    // For frontend processed tenant objects
    if (tenant.first_name || tenant.last_name) {
        return `${tenant.first_name || ''} ${tenant.last_name || ''}`.trim();
    }

    // For frontend form data
    if (tenant.firstName || tenant.lastName) {
        return `${tenant.firstName || ''} ${tenant.lastName || ''}`.trim();
    }

    return 'Unknown Tenant';
};

// Format owner display name
export const formatOwnerDisplayName = (tenant) => {
    if (!tenant) return '';

    if (tenant.ownerName) return tenant.ownerName;

    return 'No Owner';
};

// Get owner contact information
export const getOwnerContact = (tenant) => {
    if (!tenant || !tenant.ownerPhone) return '';

    return tenant.ownerPhone;
};

// Format phone number
export const formatPhoneNumber = (phone) => {
    if (!phone) return '';

    // Handle Iraqi phone numbers
    if (phone.startsWith('+964')) {
        return phone.replace(/(\+964)(\d{3})(\d{3})(\d{4})/, '$1 $2-$3-$4');
    }

    // Handle other formats
    return phone;
};

// Format currency
export const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

// Format date
export const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    }).format(date);
};

// Check if lease is expired
export const isLeaseExpired = (tenantOrApartment) => {
    if (!tenantOrApartment) return false;

    // Handle both tenant and apartment objects
    const status = tenantOrApartment.status;
    const contractEndDate = tenantOrApartment.contractEndDate || tenantOrApartment.end_date;

    if (status === 'expired') return true;
    if (!contractEndDate) return false;

    const today = new Date();
    const endDate = new Date(contractEndDate);
    // Lease ends at 12:00 PM (noon)
    endDate.setHours(12, 0, 0, 0);
    return endDate < today;
};

/**
 * Get the standardized lease status based on dates
 * Returns: 'current', 'upcoming', 'past'
 */
export const getTenantLeaseStatus = (tenant) => {
    if (!tenant) return 'unknown';

    // If status is explicitly expired, it's past
    if (tenant.status === 'expired') return 'past';

    if (!tenant.contractStartDate) return 'unknown';

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDate = new Date(tenant.contractStartDate);
    startDate.setHours(0, 0, 0, 0);

    if (startDate > today) return 'upcoming';

    if (tenant.contractEndDate) {
        const endDate = new Date(tenant.contractEndDate);
        endDate.setHours(23, 59, 59, 999);
        if (endDate < today) return 'past';
    }

    return 'current';
};

// Get rent status color
export const getRentStatusColor = (status, isDarkMode = false) => {
    const statusLower = (status || '').toLowerCase();
    const colors = {
        'paid': isDarkMode ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600',
        'pending': isDarkMode ? 'bg-zinc-800/40 text-zinc-400' : 'bg-zinc-100 text-zinc-600',
        'overdue': isDarkMode ? 'bg-rose-500/10 text-rose-400' : 'bg-rose-600/10 text-rose-600',
        'partial': isDarkMode ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600',
        'expired': isDarkMode ? 'bg-zinc-800/40 text-zinc-400' : 'bg-zinc-100 text-zinc-500'
    };

    return colors[statusLower] || (isDarkMode ? 'bg-zinc-950/20 text-zinc-400' : 'bg-gray-100 text-gray-800');
};

// Calculate rent after discount (used in frontend display)
export const calculateRentAfterDiscount = (rent, discount) => {
    const rentNum = parseFloat(rent) || 0;
    const discountNum = parseFloat(discount) || 0;
    return rentNum - discountNum;
};

// Calculate lease summary for display
export const calculateLeaseSummary = (lease) => {
    const startDate = lease.contractStartDate || lease.start_date;
    const endDate = lease.contractEndDate || lease.end_date;

    if (!startDate || !endDate) {
        return {
            durationMonths: 0,
            remainingMonths: 0,
            isExpiringSoon: false,
            totalLeaseValue: 0
        };
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();

    // Calculate duration in months - more accurate calculation
    const durationMonths = (
        (end.getFullYear() - start.getFullYear()) * 12 +
        (end.getMonth() - start.getMonth()) +
        (end.getDate() >= start.getDate() ? 0 : -1)
    );

    // Calculate remaining months using the same method
    const remainingMonths = Math.max(0,
        (end.getFullYear() - now.getFullYear()) * 12 +
        (end.getMonth() - now.getMonth()) +
        (end.getDate() >= now.getDate() ? 0 : -1)
    );

    // Check if lease is expiring soon (within 1 month)
    const isExpiringSoon = remainingMonths > 0 && remainingMonths <= 1;

    // Calculate total lease value
    const monthlyRent = parseFloat(lease.rent || lease.monthly_rent) || 0;
    const totalLeaseValue = monthlyRent * durationMonths;

    return {
        durationMonths,
        remainingMonths,
        isExpiringSoon,
        totalLeaseValue
    };
};



// Status color functions for UI
export const getLeaseStatusColor = (status, isDarkMode = false) => {
    const colors = {
        'active': isDarkMode ? 'bg-green-900 bg-opacity-20 text-green-400' : 'bg-green-100 text-green-800',
        'ended': isDarkMode ? 'bg-zinc-950 bg-opacity-20 text-zinc-400' : 'bg-gray-100 text-gray-800',
        'terminated': isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-zinc-100 text-red-800',
        'pending': isDarkMode ? 'bg-zinc-800 bg-opacity-40 text-zinc-400' : 'bg-zinc-100 text-zinc-600'
    };

    return colors[status] || (isDarkMode ? 'bg-zinc-950 bg-opacity-20 text-zinc-400' : 'bg-gray-100 text-gray-800');
};

export const getMaintenanceStatusColor = (status, isDarkMode = false) => {
    const colors = {
        'open': isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-zinc-100 text-red-800',
        'in_progress': isDarkMode ? 'bg-yellow-900 bg-opacity-20 text-yellow-400' : 'bg-yellow-100 text-yellow-800',
        'completed': isDarkMode ? 'bg-green-900 bg-opacity-20 text-green-400' : 'bg-green-100 text-green-800',
        'cancelled': isDarkMode ? 'bg-zinc-950 bg-opacity-20 text-zinc-400' : 'bg-gray-100 text-gray-800'
    };

    return colors[status] || (isDarkMode ? 'bg-zinc-950 bg-opacity-20 text-zinc-400' : 'bg-gray-100 text-gray-800');
};

export const getMaintenanceUrgencyColor = (urgency, isDarkMode = false) => {
    const colors = {
        'low': isDarkMode ? 'bg-green-900 bg-opacity-20 text-green-400' : 'bg-green-100 text-green-800',
        'normal': isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-zinc-100 text-red-800',
        'urgent': isDarkMode ? 'bg-orange-900 bg-opacity-20 text-orange-400' : 'bg-orange-100 text-orange-800',
        'emergency': isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-zinc-100 text-red-800'
    };

    return colors[urgency] || (isDarkMode ? 'bg-zinc-950 bg-opacity-20 text-zinc-400' : 'bg-gray-100 text-gray-800');
};

// Calculate days overdue based on next billing date
export const getOverdueDays = (nextBillingDate) => {
    if (!nextBillingDate) return 0;
    const today = new Date();
    const billingDate = new Date(nextBillingDate);
    if (billingDate >= today) return 0;

    const diffTime = Math.abs(today - billingDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
};

// Validate tenant data
export const validateTenantData = (tenantData) => {
    const errors = {};

    if (!tenantData.name?.trim()) {
        errors.name = 'Full name is required';
    }

    if (!tenantData.phone?.trim()) {
        errors.phone = 'Phone number is required';
    }

    if (!tenantData.rent || tenantData.rent <= 0) {
        errors.rent = 'Rent must be greater than 0';
    }

    if (!tenantData.contractStartDate) {
        errors.contractStartDate = 'Contract start date is required';
    }

    if (!tenantData.contractEndDate) {
        errors.contractEndDate = 'Contract end date is required';
    }

    if (!tenantData.paymentDate) {
        errors.paymentDate = 'Payment date is required';
    }

    if (!tenantData.apartmentId) {
        errors.apartmentId = 'Apartment is required';
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

// Tenant rent status options
export const TENANT_RENT_STATUS_OPTIONS = [
    { value: 'paid', label: 'Paid' },
    { value: 'pending', label: 'Pending' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'partial', label: 'Partial' }
];

// Profession options
export const PROFESSION_OPTIONS = [
    { value: 'accountant', label: 'Accountant' },
    { value: 'architect', label: 'Architect' },
    { value: 'artist', label: 'Artist' },
    { value: 'doctor', label: 'Doctor' },
    { value: 'engineer', label: 'Engineer' },
    { value: 'lawyer', label: 'Lawyer' },
    { value: 'teacher', label: 'Teacher' },
    { value: 'student', label: 'Student' },
    { value: 'retired', label: 'Retired' },
    { value: 'other', label: 'Other' }
];

export default {
    getAllTenants,
    getTenant,
    getTenantsByOwner,
    createTenant,
    updateTenant,
    deleteTenant,
    updateTenantStatus,
    searchTenants,
    getTenantStats,
    getTenantsWithOverdueRent,
    getTenantLeases,
    getLeaseById,
    extendLease,
    renewLease,
    transferApartment,
    endLease,
    getRentPayments,
    recordRentPayment,
    getTenantMaintenanceRequests,
    createMaintenanceRequest,
    updateMaintenanceRequestStatus,
    formatTenantDisplayName,
    formatOwnerDisplayName,
    getOwnerContact,
    formatPhoneNumber,
    formatCurrency,
    formatDate,
    getRentStatusColor,
    getLeaseStatusColor,
    getMaintenanceStatusColor,
    getMaintenanceUrgencyColor,
    calculateRentAfterDiscount,
    calculateLeaseSummary,
    validateTenantData,
    TENANT_RENT_STATUS_OPTIONS,
    PROFESSION_OPTIONS,
    getOverdueDays,
    isLeaseExpired
};
